import { Button, makeStyles, mergeClasses } from '@fluentui/react-components';
import React, { BaseSyntheticEvent, KeyboardEvent } from 'react';
import { customPalette } from '../../theme';

const TAB_KEY_PREFIX = 'corptax-tab-';
const ARROW_RIGHT_KEY_NAME = 'ArrowRight';
const ARROW_LEFT_KEY_NAME = 'ArrowLeft';
const HOME_KEY_NAME = 'Home';
const END_KEY_NAME = 'End';
const SUPPORTED_TAB_KEYS = [ARROW_RIGHT_KEY_NAME, ARROW_LEFT_KEY_NAME, HOME_KEY_NAME, END_KEY_NAME];

export interface TabListProps {
    tabs: string[];
    activeTabIndex: number;
    label: string;
    setActiveTabIndex: (value: number) => void;
}

const handleArrowKeys = (event: BaseSyntheticEvent) => {
    const keyboardEvent = event as KeyboardEvent;
    const targetElement = event.target as HTMLElement;

    if (SUPPORTED_TAB_KEYS.includes(keyboardEvent.key)) {
        event.preventDefault();
    }

    switch (keyboardEvent.key) {
        case ARROW_RIGHT_KEY_NAME:
            focusNextOrFirstTab(targetElement);
            break;
        case ARROW_LEFT_KEY_NAME:
            focusPreviousOrLastTab(targetElement);
            break;
        case HOME_KEY_NAME:
            focusFirstTab(targetElement);
            break;
        case END_KEY_NAME:
            focusLastTab(targetElement);
            break;
    }
};

const focusNextOrFirstTab = (targetElement: HTMLElement) => {
    const nextSibling = targetElement.nextSibling as HTMLElement;
    const firstTab = targetElement.parentElement?.firstChild as HTMLElement;

    if (nextSibling) {
        nextSibling.focus();
    } else {
        firstTab.focus();
    }
};

const focusPreviousOrLastTab = (targetElement: HTMLElement) => {
    const previousSibling = targetElement.previousSibling as HTMLElement;
    const lastTab = targetElement.parentElement?.lastChild as HTMLElement;

    if (previousSibling) {
        previousSibling.focus();
    } else {
        lastTab.focus();
    }
};

const focusFirstTab = (targetElement: HTMLElement) => {
    const firstTab = targetElement.parentElement?.firstChild as HTMLElement;

    if (firstTab) {
        firstTab.focus();
    }
};

const focusLastTab = (targetElement: HTMLElement) => {
    const lastTab = targetElement.parentElement?.lastChild as HTMLElement;

    if (lastTab) {
        lastTab.focus();
    }
};

const useStyles = makeStyles({
    baseTab: {
        fontSize: '12px',
        fontWeight: 500,
        borderTopWidth: 0,
        borderRightWidth: 0,
        borderBottomWidth: 0,
        borderLeftWidth: 0,
        borderTopStyle: 'none',
        borderRightStyle: 'none',
        borderBottomStyle: 'none',
        borderLeftStyle: 'none',

        lineHeight: '22px',
    },
    tabsWrapper: {
        display: 'flex',
        flexDirection: 'row',
        paddingTop: '8px',
        paddingLeft: '10px',
    },
    tab: {
        color: customPalette.blueDark,
    },
    activeTab: {
        backgroundColor: customPalette.themeLighter,
        color: customPalette.themePrimary,
    },
});

const TabList: React.FC<TabListProps> = ({ tabs, activeTabIndex, label, setActiveTabIndex }) => {
    const styles = useStyles();

    const filterTabs = tabs.map((tab, index) => {
        const isActiveTab = index === activeTabIndex;
        return (
            <Button
                appearance='secondary'
                aria-label={tab}
                role='tab'
                aria-selected={isActiveTab}
                tabIndex={isActiveTab ? 0 : -1}
                onKeyDown={handleArrowKeys}
                key={TAB_KEY_PREFIX + index}
                className={mergeClasses(styles.baseTab, isActiveTab ? styles.activeTab : styles.tab)}
                onClick={() => setActiveTabIndex(index)}
            >
                {tab}
            </Button>
        );
    });

    return (
        <div role='tablist' aria-label={label} className={styles.tabsWrapper}>
            {filterTabs}
        </div>
    );
};

export default TabList;
